"use client";

// NOTE: patched from bklit registry. If you re-install @bklit/chart-tooltip or the
// tooltip subtree, re-apply this file (mode prop, rounded-sm, CSS-var driven bg/fg/shadow).
// Refinement-plan §D.6 + §P0.

import { motion, useSpring } from "motion/react";
import { memo, useMemo, useRef } from "react";

const TICKER_ITEM_HEIGHT = 24;
const COMPACT_TICKER_THRESHOLD = 60;

const MONTH_TOKEN_RE = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/i;

// Literal colors, not tokens: a module's token scope pins `--fg` dark and
// `--card` light for its own surface, and `--background` still follows the app
// theme — either one would put dark text on this dark pill.
const PILL_CLASS =
  "overflow-hidden rounded-sm px-3 py-1 shadow-none " +
  "bg-neutral-900 text-neutral-50";

export type DateTickerMode = "auto" | "date" | "value";

export interface DateTickerProps {
  currentIndex: number;
  labels: string[];
  visible: boolean;
  mode?: DateTickerMode;
}

function detectMode(labels: string[]): "date" | "value" {
  if (labels.length === 0) return "value";
  const sample = labels[0] ?? "";
  const parts = sample.split(" ");
  if (parts.length < 2) return "value";
  return MONTH_TOKEN_RE.test(parts[0] ?? "") ? "date" : "value";
}

/**
 * Longest whole-word prefix shared by every label. E.g. for
 * ["Woche 1", "Woche 2", ..., "Woche 12"] returns "Woche".
 * Returns "" when labels don't share a word-aligned prefix.
 */
function commonWordPrefix(labels: string[]): string {
  if (labels.length < 2) return "";
  const first = (labels[0] ?? "").trim();
  const words = first.split(/\s+/);
  if (words.length < 2) return "";
  for (let n = words.length - 1; n >= 1; n--) {
    const prefix = `${words.slice(0, n).join(" ")} `;
    if (labels.every((l) => l.startsWith(prefix))) {
      return prefix.trimEnd();
    }
  }
  return "";
}

const DateTickerCompact = memo(function DateTickerCompact({
  currentIndex,
  labels,
}: Omit<DateTickerProps, "visible" | "mode">) {
  const label = labels[currentIndex] ?? labels[0] ?? "";

  return (
    <div className={PILL_CLASS}>
      <div className="flex h-6 items-center justify-center">
        <span className="whitespace-nowrap font-medium text-sm">{label}</span>
      </div>
    </div>
  );
});

/** Static prefix + vertically-scrolling suffix. Used when labels share a word prefix. */
const PrefixedValueTicker = memo(function PrefixedValueTicker({
  currentIndex,
  labels,
  prefix,
}: Omit<DateTickerProps, "visible" | "mode"> & { prefix: string }) {
  const suffixes = useMemo(
    () => labels.map((l) => l.slice(prefix.length).trimStart()),
    [labels, prefix],
  );
  const suffixY = useSpring(0, { stiffness: 400, damping: 35 });
  suffixY.set(-currentIndex * TICKER_ITEM_HEIGHT);

  return (
    <div className={PILL_CLASS}>
      <div className="relative h-6 overflow-hidden">
        <div className="flex items-center justify-center gap-1">
          <div className="flex h-6 shrink-0 items-center justify-center">
            <span className="whitespace-nowrap font-medium text-sm">
              {prefix}
            </span>
          </div>
          <div className="relative h-6 overflow-hidden">
            <motion.div className="flex flex-col" style={{ y: suffixY }}>
              {suffixes.map((s, index) => (
                <div
                  className="flex h-6 shrink-0 items-center justify-center"
                  key={`${s}-${index}`}
                >
                  <span className="whitespace-nowrap font-medium text-sm tabular-nums">
                    {s}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
});

const ValueTicker = memo(function ValueTicker({
  currentIndex,
  labels,
}: Omit<DateTickerProps, "visible" | "mode">) {
  const label = labels[currentIndex] ?? labels[0] ?? "";

  return (
    <div className={PILL_CLASS}>
      <div className="flex h-6 items-center justify-center">
        <span className="whitespace-nowrap font-medium text-sm">{label}</span>
      </div>
    </div>
  );
});

const DateTickerInner = memo(function DateTickerInner({
  currentIndex,
  labels,
}: Omit<DateTickerProps, "visible" | "mode">) {
  const parsedLabels = useMemo(() => {
    return labels.map((label, index) => {
      const parts = label.split(" ");
      const month = parts[0] || "";
      const day = parts[1] || "";
      return { month, day, full: label, key: `${label}::${index}` };
    });
  }, [labels]);

  const monthSegments = useMemo(() => {
    const segments: { month: string; key: string; startIndex: number }[] = [];

    parsedLabels.forEach((label, index) => {
      const prev = segments.at(-1);
      if (!prev || prev.month !== label.month) {
        segments.push({
          month: label.month,
          key: `${label.month}-${index}`,
          startIndex: index,
        });
      }
    });

    return segments;
  }, [parsedLabels]);

  const currentMonthIndex = useMemo(() => {
    if (currentIndex < 0 || currentIndex >= parsedLabels.length) {
      return 0;
    }
    for (let i = monthSegments.length - 1; i >= 0; i--) {
      const segment = monthSegments[i];
      if (segment && segment.startIndex <= currentIndex) {
        return i;
      }
    }
    return 0;
  }, [currentIndex, parsedLabels.length, monthSegments]);

  const prevMonthIndexRef = useRef(-1);

  const dayY = useSpring(0, { stiffness: 400, damping: 35 });
  const monthY = useSpring(0, { stiffness: 400, damping: 35 });

  dayY.set(-currentIndex * TICKER_ITEM_HEIGHT);

  if (currentMonthIndex >= 0) {
    const isFirstRender = prevMonthIndexRef.current === -1;
    const monthChanged = prevMonthIndexRef.current !== currentMonthIndex;
    if (isFirstRender || monthChanged) {
      monthY.set(-currentMonthIndex * TICKER_ITEM_HEIGHT);
      prevMonthIndexRef.current = currentMonthIndex;
    }
  }

  return (
    <div className={PILL_CLASS}>
      <div className="relative h-6 overflow-hidden">
        <div className="flex items-center justify-center gap-1">
          <div className="relative h-6 overflow-hidden">
            <motion.div className="flex flex-col" style={{ y: monthY }}>
              {monthSegments.map((segment) => (
                <div
                  className="flex h-6 shrink-0 items-center justify-center"
                  key={segment.key}
                >
                  <span className="whitespace-nowrap font-medium text-sm">
                    {segment.month}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="relative h-6 overflow-hidden">
            <motion.div className="flex flex-col" style={{ y: dayY }}>
              {parsedLabels.map((label) => (
                <div
                  className="flex h-6 shrink-0 items-center justify-center"
                  key={label.key}
                >
                  <span className="whitespace-nowrap font-medium text-sm">
                    {label.day}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
});

export function DateTicker({
  currentIndex,
  labels,
  visible,
  mode = "auto",
}: DateTickerProps) {
  const resolvedMode = mode === "auto" ? detectMode(labels) : mode;
  const prefix = useMemo(
    () => (resolvedMode === "value" ? commonWordPrefix(labels) : ""),
    [labels, resolvedMode],
  );

  if (!visible || labels.length === 0) {
    return null;
  }

  if (resolvedMode === "value") {
    if (prefix) {
      return (
        <PrefixedValueTicker
          currentIndex={currentIndex}
          labels={labels}
          prefix={prefix}
        />
      );
    }
    return <ValueTicker currentIndex={currentIndex} labels={labels} />;
  }

  if (labels.length > COMPACT_TICKER_THRESHOLD) {
    return <DateTickerCompact currentIndex={currentIndex} labels={labels} />;
  }

  return <DateTickerInner currentIndex={currentIndex} labels={labels} />;
}

DateTicker.displayName = "DateTicker";

export default DateTicker;
